/**
 * SessionSummaryPage — Post-session Assessment Results (Screen 4).
 *
 * Route: /app/session/:sessionId/summary
 *
 * All original AC preserved:
 * - AC-1: "End Session" confirmation dialog (handled upstream in SessionPage)
 * - AC-2: Confirmed end → POST /sessions/{id}/end + WebSocket close
 * - AC-3: Score summary with overall %, pass/fail badge, competency bars, feedback, violations
 * - AC-4: Compliance guardrail active on scoring LLM (upstream)
 * - AC-5: "Start New Session" clears sessionStorage, navigates to /app/scenarios
 * - AC-6: Fully internationalised
 * - AC-7: Server-triggered session_ended handled without confirmation dialog
 * - AC-8: Score polling every 2s, up to 15 attempts; graceful timeout message
 *
 * Visual redesign (Screen 4 wireframe):
 * - Score circle with large percentage
 * - Pass/Fail badge + "Download Certificate" button (when passed)
 * - Retake Assessment + Start New Session CTAs
 * - Performance by Competency in a card
 * - Overall Feedback card
 * - Compliance violations accordion
 * - Trophy icon for pass
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { StarRating } from '../components/StarRating';
import styles from './SessionSummaryPage.module.css';

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

const ADMIN_PORTAL_URL: string =
  (import.meta.env.VITE_ADMIN_PORTAL_URL as string | undefined) ??
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? `${window.location.protocol}//${window.location.hostname}:5174/admin/`
    : `${window.location.origin}/admin/`);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ComplianceViolation {
  turn: number;
  rule_id: string;
  trigger_phrase: string;
  guidance: string;
}

interface ScoreData {
  session_id: string;
  overall_score: number;
  passed: boolean;
  incomplete: boolean | undefined;
  competency_scores: {
    communication: number;
    product_knowledge: number;
    compliance: number;
    objection_handling: number;
    closing?: number;
  };
  llm_feedback: string;
  strengths: string[];
  weaknesses: string[];
  improvement_points: string[];
  compliance_violations: ComplianceViolation[];
}

interface SessionDetailResponse {
  score?: {
    overall_score?: number;
    passed?: boolean;
    communication?: number;
    product_knowledge?: number;
    compliance?: number;
    objection_handling?: number;
    closing?: number;
    llm_feedback?: string;
    strengths?: string[] | string;
    weaknesses?: string[] | string;
    improvement_points?: string[] | string;
  } | null;
  status?: string;
  compliance_events?: Array<{
    turn?: number;
    rule_id?: string;
    trigger_phrase?: string;
    guidance?: string;
  }>;
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'polling'; attempt: number }
  | { kind: 'ready'; score: ScoreData }
  | { kind: 'timeout' }
  | { kind: 'error'; message: string };

type PollSource = '/score' | '/detail' | 'idle';

const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 2000;
const PASS_THRESHOLD = 70; // percentage

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

async function fetchScore(token: string, sessionId: string): Promise<ScoreData | null> {
  const res = await fetch(`/api/v1/sessions/${sessionId}/score`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const body = await res.json();
  if (!body.data.score_ready) return null;
  return normalizeScoreData(body.data as Partial<ScoreData>, sessionId);
}

async function fetchScoreFromDetail(token: string, sessionId: string): Promise<ScoreData | null> {
  const res = await fetch(`/api/v1/sessions/${sessionId}/detail`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;

  const body = await res.json();
  const detail = body.data as SessionDetailResponse | undefined;
  // Legacy replay payloads are intentionally ignored on the summary page.
  // The training portal only surfaces score, feedback, and compliance notes.
  const score = detail?.score;
  if (!score) return null;

  return normalizeScoreData(
    {
      session_id: sessionId,
      overall_score: Number(score.overall_score ?? 0),
      passed: Boolean(score.passed),
      incomplete: detail?.status !== 'completed' && detail?.status !== 'ended',
      competency_scores: {
        communication: Number(score.communication ?? 0),
        product_knowledge: Number(score.product_knowledge ?? 0),
        compliance: Number(score.compliance ?? 0),
        objection_handling: Number(score.objection_handling ?? 0),
        closing: Number(score.closing ?? 0),
      },
      llm_feedback: String(score.llm_feedback ?? ''),
      strengths: normalizeFeedbackList(score.strengths),
      weaknesses: normalizeFeedbackList(score.weaknesses),
      improvement_points: normalizeFeedbackList(score.improvement_points),
      compliance_violations: Array.isArray(detail?.compliance_events)
        ? detail.compliance_events.map((event: any) => ({
            turn: Number(event.turn ?? 0),
            rule_id: String(event.rule_id ?? ''),
            trigger_phrase: String(event.trigger_phrase ?? ''),
            guidance: String(event.guidance ?? ''),
          }))
        : [],
    },
    sessionId,
  );
}

function normalizeFeedbackList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item).trim()).filter(Boolean);
      }
    } catch {
      return [trimmed];
    }
  }
  return [];
}

function normalizeScoreData(raw: Partial<ScoreData>, sessionId: string): ScoreData {
  return {
    session_id: raw.session_id ?? sessionId,
    overall_score: Number(raw.overall_score ?? 0),
    passed: Boolean(raw.passed),
    incomplete: raw.incomplete,
    competency_scores: {
      communication: Number(raw.competency_scores?.communication ?? 0),
      product_knowledge: Number(raw.competency_scores?.product_knowledge ?? 0),
      compliance: Number(raw.competency_scores?.compliance ?? 0),
      objection_handling: Number(raw.competency_scores?.objection_handling ?? 0),
      closing: Number(raw.competency_scores?.closing ?? 0),
    },
    llm_feedback: String(raw.llm_feedback ?? ''),
    strengths: normalizeFeedbackList(raw.strengths),
    weaknesses: normalizeFeedbackList(raw.weaknesses),
    improvement_points: normalizeFeedbackList(raw.improvement_points),
    compliance_violations: Array.isArray(raw.compliance_violations)
      ? raw.compliance_violations.map((event) => ({
          turn: Number(event.turn ?? 0),
          rule_id: String(event.rule_id ?? ''),
          trigger_phrase: String(event.trigger_phrase ?? ''),
          guidance: String(event.guidance ?? ''),
        }))
      : [],
  };
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function CompetencyBar({
  label,
  score,
}: {
  label: string;
  score: number;
}): JSX.Element {
  const pct = Math.round(score * 100);
  return (
    <div className={styles.competencyRow}>
      <span className={styles.competencyLabel}>{label}</span>
      <div
        className={styles.barTrack}
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label}: ${pct}%`}
      >
        <div className={styles.barFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={styles.competencyPct}>{pct}%</span>
    </div>
  );
}

function FeedbackSection({
  title,
  items,
}: {
  title: string;
  items: string[];
}): JSX.Element {
  return (
    <div className={styles.feedbackSection}>
      <h3 className={styles.feedbackSectionTitle}>{title}</h3>
      {items.length === 0 ? (
        <p className={styles.feedbackEmpty}>No items identified.</p>
      ) : (
        <ul className={styles.feedbackList}>
          {items.map((item, index) => (
            <li key={`${title}-${index}`} className={styles.feedbackItem}>
              {item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function SessionSummaryPage(): JSX.Element {
  const { t } = useTranslation('training');
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const token = sessionStorage.getItem('access_token') ?? '';
  const scenarioTitle = sessionStorage.getItem('scenario_title') ?? t('summary.title');
  const showDebug = new URLSearchParams(location.search).get('debug') === '1';
  const feedbackCourseId = sessionStorage.getItem('course_feedback_course_id') ?? '';
  const feedbackEnrollmentId = sessionStorage.getItem('course_feedback_enrollment_id') ?? '';
  const feedbackCourseTitle = sessionStorage.getItem('course_feedback_course_title') ?? scenarioTitle;
  const feedbackSectionTitle = feedbackCourseId ? 'Your Course Feedback' : 'Your Session Feedback';
  const feedbackSectionSubtitle = feedbackCourseId
    ? `Share a quick rating for ${feedbackCourseTitle}.`
    : 'Share a quick rating for this training session.';

  const [pageState, setPageState] = useState<PageState>({ kind: 'loading' });
  const [debugPollSource, setDebugPollSource] = useState<PollSource>('idle');
  const [debugPollAttempt, setDebugPollAttempt] = useState<number>(0);
  const [learnerRating, setLearnerRating] = useState<number>(0);
  const [learnerFeedback, setLearnerFeedback] = useState('');
  const [feedbackState, setFeedbackState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [showTip, setShowTip] = useState(false);

  // -------------------------------------------------------------------------
  // Score loading (AC-3, AC-8)
  // -------------------------------------------------------------------------

  const loadScore = useCallback(async (): Promise<void> => {
    if (!sessionId) return;
    setPageState({ kind: 'polling', attempt: 0 });

    for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
      setPageState({ kind: 'polling', attempt });
      setDebugPollAttempt(attempt + 1);
      setDebugPollSource('/score');
      const scoreFromScore = await fetchScore(token, sessionId);
      if (scoreFromScore) {
        setDebugPollSource('/score');
        setPageState({ kind: 'ready', score: scoreFromScore });

        // Record module progress if started from a course module
        const moduleId = sessionStorage.getItem('module_id');
        const enrollmentId = sessionStorage.getItem('enrollment_id');
        if (moduleId && enrollmentId) {
          try {
            await fetch('/api/v1/module-progress', {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                enrollment_id: enrollmentId,
                module_id: moduleId,
                score: scoreFromScore.overall_score * 100,
                session_id: sessionId,
              }),
            });
            sessionStorage.removeItem('module_id');
            sessionStorage.removeItem('enrollment_id');
          } catch (err) {
            console.error('Failed to record module progress:', err);
          }
        }
        return;
      }

      setDebugPollSource('/detail');
      const score = await fetchScoreFromDetail(token, sessionId);
      if (score) {
        setDebugPollSource('/detail');
        setPageState({ kind: 'ready', score });

        // Record module progress if started from a course module
        const moduleId = sessionStorage.getItem('module_id');
        const enrollmentId = sessionStorage.getItem('enrollment_id');
        if (moduleId && enrollmentId) {
          try {
            await fetch('/api/v1/module-progress', {
              method: 'POST',
              headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                enrollment_id: enrollmentId,
                module_id: moduleId,
                score: score.overall_score * 100,
                session_id: sessionId,
              }),
            });
            sessionStorage.removeItem('module_id');
            sessionStorage.removeItem('enrollment_id');
          } catch (err) {
            console.error('Failed to record module progress:', err);
          }
        }
        return;
      }
      await sleep(POLL_INTERVAL_MS);
    }

    setPageState({ kind: 'timeout' });
  }, [sessionId, token]);

  useEffect(() => {
    void loadScore();
    sessionStorage.removeItem('ws_url');
    sessionStorage.removeItem('session_id');
  }, [loadScore]);

  useEffect(() => {
    if (pageState.kind !== 'timeout' || !sessionId) return;
    const currentSessionId = sessionId;
    let cancelled = false;

    async function retryUntilReady(): Promise<void> {
      while (!cancelled) {
        setDebugPollSource('/score');
        const scoreFromScore = await fetchScore(token, currentSessionId);
        if (scoreFromScore) {
          setDebugPollSource('/score');
          setPageState({ kind: 'ready', score: scoreFromScore });
          return;
        }

        setDebugPollSource('/detail');
        const score = await fetchScoreFromDetail(token, currentSessionId);
        if (score) {
          setDebugPollSource('/detail');
          setPageState({ kind: 'ready', score });
          return;
        }
        await sleep(10_000);
      }
    }

    void retryUntilReady();
    return () => {
      cancelled = true;
    };
  }, [pageState.kind, sessionId, token]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  function handleStartNew(): void {
    sessionStorage.removeItem('ws_url');
    sessionStorage.removeItem('session_id');
    sessionStorage.removeItem('course_feedback_course_id');
    sessionStorage.removeItem('course_feedback_enrollment_id');
    sessionStorage.removeItem('course_feedback_course_title');
    navigate('/scenarios');
  }

  function handleRetake(): void {
    sessionStorage.removeItem('course_feedback_course_id');
    sessionStorage.removeItem('course_feedback_enrollment_id');
    sessionStorage.removeItem('course_feedback_course_title');
    navigate('/scenarios');
  }

  function handleRatingSelect(value: number): void {
    setLearnerRating(value);
    setShowTip(true);
    window.setTimeout(() => {
      setShowTip(false);
    }, 4000);
  }

  async function handleSubmitFeedback(): Promise<void> {
    if (!sessionId || learnerRating < 1) {
      setFeedbackState('error');
      return;
    }
    try {
      setFeedbackState('saving');
      if (feedbackCourseId) {
        await apiClient.post(`/courses/${feedbackCourseId}/feedback`, {
          session_id: sessionId,
          enrollment_id: feedbackEnrollmentId || null,
          rating: learnerRating,
          feedback: learnerFeedback,
        });
      } else {
        await apiClient.post(`/sessions/${sessionId}/feedback`, {
          rating: learnerRating,
          feedback: learnerFeedback,
        });
      }
      setFeedbackState('saved');
    } catch {
      setFeedbackState('error');
    }
  }

  async function handleDownloadCertificate(): Promise<void> {
    if (!sessionId) return;
    // Fetch with the Bearer token (the endpoint requires auth — a bare
    // window.open cannot send the Authorization header), then open the PDF
    // as an object URL so it works in a new tab / download.
    try {
      const res = await fetch(`/api/v1/sessions/${sessionId}/certificate`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        console.error('Certificate download failed:', res.status);
        return;
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      // Revoke shortly after to let the new tab load it.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      console.error('Certificate download error:', err);
    }
  }

  // -------------------------------------------------------------------------
  // Loading / timeout states
  // -------------------------------------------------------------------------

  if (pageState.kind === 'loading' || pageState.kind === 'polling') {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>
          <div className={styles.spinner} aria-hidden="true" />
          <p>{t('summary.scoreCalculating')}</p>
          {showDebug && (
            <p className={styles.debugStatus}>
              Debug: checking {debugPollSource} (attempt {debugPollAttempt || 1})
            </p>
          )}
        </div>
      </div>
    );
  }

  if (pageState.kind === 'timeout') {
    return (
      <div className={styles.page}>
        <div className={styles.timeoutMessage} role="alert">
          <p>{t('summary.scoreTimeout')}</p>
          {showDebug && (
            <p className={styles.debugStatus}>
              Debug: last checked {debugPollSource} after {debugPollAttempt} attempts
            </p>
          )}
          <button className={styles.btnPrimary} onClick={handleStartNew}>
            {t('summary.cta.startNew')}
          </button>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Score ready
  // -------------------------------------------------------------------------

  if (pageState.kind !== 'ready') return <></>;

  const { score } = pageState;
  const pct = Math.round(score.overall_score * 100);
  const passed = score.passed;
  // Trial Play is a practice session — it does not count toward certification,
  // so we present it as practice feedback rather than a pass/fail assessment.
  const isTrial = sessionStorage.getItem('session_mode') === 'trial';
  const adminSessionUrl = sessionId
    ? `${ADMIN_PORTAL_URL.replace(/\/$/, '')}/sessions/${sessionId}`
    : `${ADMIN_PORTAL_URL.replace(/\/$/, '')}/sessions`;
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric', month: 'long', year: 'numeric',
  });

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.assessmentHeader}>
        <h1 className={styles.assessmentTitle}>
          {scenarioTitle} {isTrial ? 'Practice Session' : 'Assessment'}
        </h1>
        <p className={styles.assessmentMeta}>Completed {today}</p>
      </div>

      {isTrial && (
        <div className={styles.assessmentMeta} style={{ margin: '0 0 1rem', color: '#0055a5', fontWeight: 600 }} role="note">
          💡 Trial Play (practice) — this session is for learning and is not scored or counted toward certification.
        </div>
      )}

      {/* Main card layout */}
      <div className={styles.mainGrid}>
        {/* Left — score card */}
        <div className={styles.scoreCard}>
          {/* Score circle — neutral framing for practice (trial) sessions */}
          <div className={`${styles.scoreCircle} ${isTrial ? styles.scoreCirclePassed : (passed ? styles.scoreCirclePassed : styles.scoreCircleFailed)}`}>
            <span className={styles.scorePct}>{isTrial ? '—' : `${pct}%`}</span>
            <span className={styles.scoreLabel}>{isTrial ? 'Practice' : t('summary.overallScore')}</span>
          </div>

          {/* Badge: practice for trial mode, pass/fail otherwise */}
          {isTrial ? (
            <span className={`${styles.passBadge} ${styles.passed}`}>🎓 Practice Session</span>
          ) : (
            <span className={`${styles.passBadge} ${passed ? styles.passed : styles.failed}`}>
              {passed ? `✓ ${t('summary.passed')}` : t('summary.notYetPassed')}
            </span>
          )}

          <p className={styles.passingScoreNote}>
            {isTrial ? 'Not counted toward certification' : `Passing Score: ${PASS_THRESHOLD}%`}
          </p>

          {/* Trophy for passing (assessment only) */}
          {!isTrial && passed && (
            <div className={styles.trophy} aria-label="Achievement unlocked">
              🏆
            </div>
          )}

          {/* CTAs */}
          <div className={styles.ctaColumn}>
            <a
              className={styles.btnAdmin}
              href={adminSessionUrl}
            >
              Open in Admin
            </a>
            {passed && (
              <button className={styles.btnCertificate} onClick={() => void handleDownloadCertificate()}>
                ⬇ Download Certificate
              </button>
            )}
            <button className={styles.btnRetake} onClick={handleRetake}>
              Retake Assessment
            </button>
            <button className={styles.btnPrimary} onClick={handleStartNew}>
              {t('summary.cta.startNew')}
            </button>
          </div>
        </div>

        {/* Right — competency + feedback */}
        <div className={styles.detailCol}>
          {/* Performance by Competency */}
          <section className={styles.section} aria-labelledby="competency-heading">
            <h2 id="competency-heading" className={styles.sectionTitle}>
              Performance by Competency
            </h2>
            <CompetencyBar
              label={t('summary.competency.communication')}
              score={score.competency_scores.communication}
            />
            <CompetencyBar
              label={t('summary.competency.productKnowledge')}
              score={score.competency_scores.product_knowledge}
            />
            <CompetencyBar
              label={t('summary.competency.compliance')}
              score={score.competency_scores.compliance}
            />
            <CompetencyBar
              label={t('summary.competency.objectionHandling')}
              score={score.competency_scores.objection_handling}
            />
            <CompetencyBar
              label={t('summary.competency.closing')}
              score={score.competency_scores.closing ?? 0}
            />
          </section>

          {/* Overall Feedback */}
          <section className={styles.section} aria-labelledby="feedback-heading">
            <h2 id="feedback-heading" className={styles.sectionTitle}>
              {t('summary.feedback.title')}
            </h2>
            {score.llm_feedback && (
              <p className={styles.feedbackText}>{score.llm_feedback}</p>
            )}
            <div className={styles.feedbackGrid}>
              <FeedbackSection title="Strengths" items={score.strengths} />
              <FeedbackSection title="Weaknesses" items={score.weaknesses} />
              <FeedbackSection title="Improvement Points" items={score.improvement_points} />
            </div>
          </section>

          {/* Compliance violations */}
          <section className={styles.section} aria-labelledby="violations-heading">
            <h2 id="violations-heading" className={styles.sectionTitle}>
              {t('summary.violations.title')}
            </h2>
            {score.compliance_violations.length === 0 ? (
              <p className={styles.noViolations}>{t('summary.violations.none')}</p>
            ) : (
              <ul className={styles.violationList}>
                {score.compliance_violations.map((v, i) => (
                  <li key={i} className={styles.violationItem}>
                    <div className={styles.violationHeader}>
                      <span className={styles.ruleId}>
                        {t('summary.violations.ruleId')}: {v.rule_id}
                      </span>
                      <span className={styles.turnLabel}>Turn {v.turn}</span>
                    </div>
                    <p className={styles.triggerPhrase}>
                      <strong>{t('summary.violations.triggerPhrase')}: </strong>
                      &ldquo;{v.trigger_phrase}&rdquo;
                    </p>
                    <p className={styles.guidance}>
                      <strong>{t('summary.violations.guidance')}: </strong>
                      {v.guidance}
                    </p>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className={styles.section} aria-labelledby="learner-feedback-heading">
            <div className={styles.feedbackHeaderRow}>
              <div>
                <h2 id="learner-feedback-heading" className={styles.sectionTitle}>
                  {feedbackSectionTitle}
                </h2>
                <p className={styles.learnerFeedbackMeta}>
                  {feedbackSectionSubtitle}
                </p>
              </div>
              {showTip && (
                <span className={styles.ratingTip}>
                  Tip: click a star to set your rating out of 5.
                </span>
              )}
            </div>

            <div className={styles.learnerFeedbackForm}>
              <div className={styles.ratingPicker}>
                <StarRating
                  value={learnerRating}
                  onChange={handleRatingSelect}
                  readOnly={false}
                  size="lg"
                  ariaLabel={`Selected rating ${learnerRating || 0} out of 5`}
                />
                <span className={styles.ratingSelectionText}>
                  {learnerRating > 0 ? `${learnerRating}/5 selected` : 'Select a rating'}
                </span>
              </div>

              <label className={styles.feedbackField}>
                <span className={styles.feedbackFieldLabel}>What did you think?</span>
                <textarea
                  className={styles.feedbackTextarea}
                  rows={4}
                  value={learnerFeedback}
                  onChange={(event) => setLearnerFeedback(event.target.value)}
                  placeholder="Tell us what worked well and what could be improved."
                />
              </label>

              <div className={styles.feedbackActions}>
                <button
                  type="button"
                  className={styles.btnPrimary}
                  onClick={() => void handleSubmitFeedback()}
                  disabled={feedbackState === 'saving'}
                >
                  {feedbackState === 'saving' ? 'Saving…' : 'Submit Feedback'}
                </button>
                {feedbackState === 'saved' && (
                  <span className={styles.feedbackSaved}>Thanks — your feedback has been saved.</span>
                )}
                {feedbackState === 'error' && (
                  <span className={styles.feedbackError}>Please select a rating and try again.</span>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
